// ── Creator Rank & XP System ──────────────────────────────────────────────────

const RANKS = [
  { name: 'Open Mic',      xp: 0,    desc: 'Every legend starts somewhere' },
  { name: 'Warm Up Act',   xp: 50,   desc: 'Finding your voice' },
  { name: 'On Air',        xp: 150,  desc: 'The mic is hot' },
  { name: 'In The Mix',    xp: 400,  desc: 'Consistently creating' },
  { name: 'Showrunner',    xp: 800,  desc: 'Running the show' },
  { name: 'Headliner',     xp: 1500, desc: 'Top of the bill' },
  { name: 'Hall of Fame',  xp: 3000, desc: 'Podcast royalty' },
  { name: 'Icon',          xp: 6000, desc: 'They named a mic after you' },
];

const XP_ACTIONS = {
  episode_built:      25,
  trend_used:         15,
  streak_day:         10,
  streak_bonus_7:     50,
  streak_bonus_30:    200,
  first_episode:      50,
  niche_setup:        20,
  fifth_episode:      75,
  tenth_episode:      100,
  used_suggestion:    5,
  // ── Engagement actions (clips feed) ────────────────────────────────
  // Small per-action XP so the gamification responds to real platform
  // use, not just builds. Streak multiplier still applies.
  collab_suggest:     8,
  comment_posted:     3,
  clip_remixed:       10,
  // Milestone bonuses for the collab loop. Awarded by the collab
  // tracker (see recordCollabSuggest below) — not callable directly.
  collab_milestone_10:  100,
  collab_milestone_20:  200,
  collab_milestone_50:  500,
  collab_milestone_100: 1200,
};

// Collab milestone definitions. Each tier awards XP + a credit reward
// that the server-side trigger grants (see TODO note in
// recordCollabSuggest). Surfaced in the XP & Achievements settings
// section so users see what they're working toward.
const COLLAB_MILESTONES = [
  { count: 10,  xpKey: 'collab_milestone_10',  credits: 25,  title: 'First Ten',     desc: 'Suggested 10 collabs — you\'re a connector' },
  { count: 20,  xpKey: 'collab_milestone_20',  credits: 50,  title: 'Network Effect',desc: '20 collabs and counting' },
  { count: 50,  xpKey: 'collab_milestone_50',  credits: 100, title: 'Bridge Builder',desc: '50 collabs — you make this place go' },
  { count: 100, xpKey: 'collab_milestone_100', credits: 200, title: 'The Hub',       desc: '100 collabs — collab hall of fame' },
];

// Achievement definitions — podcast puns and milestones
const ACHIEVEMENTS = [
  { id: 'first_episode',  title: 'Mic Drop',           desc: 'Built your first episode',                    xpReq: null, action: 'first_episode' },
  { id: 'fifth_episode',  title: 'Five & Alive',       desc: '5 episodes in the vault — you\'re legit',    xpReq: null, action: 'fifth_episode' },
  { id: 'tenth_episode',  title: 'Double Digits',      desc: '10 episodes — most creators never get here', xpReq: null, action: 'tenth_episode' },
  { id: 'streak_3',       title: 'Hat Trick',          desc: '3-day streak — building momentum',            xpReq: null, streak: 3 },
  { id: 'streak_7',       title: 'Weekly Regular',     desc: '7-day streak — this is a habit now',          xpReq: null, streak: 7 },
  { id: 'streak_30',      title: 'Iron Mic',           desc: '30-day streak — you can\'t be stopped',      xpReq: null, streak: 30 },
  { id: 'rank_on_air',    title: 'On Air',             desc: 'Reached On Air rank',                         xpReq: 150 },
  { id: 'rank_mix',       title: 'In The Mix',         desc: 'Reached In The Mix rank',                     xpReq: 400 },
  { id: 'rank_showrunner',title: 'Running the Show',   desc: 'Reached Showrunner rank',                     xpReq: 800 },
  { id: 'rank_headliner', title: 'Headliner',          desc: 'Top of the bill',                             xpReq: 1500 },
  { id: 'rank_hof',       title: 'Hall of Fame',       desc: 'Podcast royalty status',                      xpReq: 3000 },
  { id: 'trend_scout',    title: 'Trend Scout',        desc: 'Used 5 trending topics for episodes',         xpReq: null, trendCount: 5 },
  { id: 'niche_dialed',   title: 'Dialed In',          desc: 'Set up your niche intelligence profile',      xpReq: null, action: 'niche_setup' },
  // Collab milestones — earned via the collab tracker on the Cuts feed.
  { id: 'collab_10',      title: 'First Ten',          desc: '10 collab suggestions sent — earns 25 credits',  xpReq: null, collabCount: 10  },
  { id: 'collab_20',      title: 'Network Effect',     desc: '20 collab suggestions sent — earns 50 credits',  xpReq: null, collabCount: 20  },
  { id: 'collab_50',      title: 'Bridge Builder',     desc: '50 collab suggestions sent — earns 100 credits', xpReq: null, collabCount: 50  },
  { id: 'collab_100',     title: 'The Hub',            desc: '100 collab suggestions sent — earns 200 credits',xpReq: null, collabCount: 100 },
];

const STORAGE_KEY = 'episode_xp';
const POPUP_KEY = 'achievement_queue';

// ── Get or initialize XP state ────────────────────────────────────────────────
export function getXPState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    xp: 0,
    actions: [],
    streak: 0,
    lastDate: null,
    milestones: [],
    trendUseCount: 0,
    // Collab loop counter (per-user, localStorage). Increment via
    // recordCollabSuggest. Drives the collab_10/20/50/100 milestone
    // achievements + their XP + credit-reward bonuses.
    collabSuggestCount: 0,
    // Pending milestone credit rewards waiting on a server-side grant.
    // Each entry: { milestoneId, credits, ts }. Cleared once the
    // server-side trigger awards them (TODO — currently surfaced in
    // the Settings UI as "pending" so users see what's owed).
    pendingCreditRewards: [],
  };
}

function saveXPState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

// ── Achievement popup queue ───────────────────────────────────────────────────
function queueAchievement(achievement) {
  try {
    const queue = JSON.parse(localStorage.getItem(POPUP_KEY) || '[]');
    queue.push({ ...achievement, ts: Date.now() });
    localStorage.setItem(POPUP_KEY, JSON.stringify(queue));
  } catch {}
}

export function popAchievement() {
  try {
    const queue = JSON.parse(localStorage.getItem(POPUP_KEY) || '[]');
    if (!queue.length) return null;
    const next = queue.shift();
    localStorage.setItem(POPUP_KEY, JSON.stringify(queue));
    return next;
  } catch { return null; }
}

// ── Check and award achievements ──────────────────────────────────────────────
function checkAchievements(state) {
  for (const ach of ACHIEVEMENTS) {
    if (state.milestones.includes(ach.id)) continue;

    let earned = false;
    if (ach.action && state.milestones.includes(ach.action)) earned = true;
    if (ach.xpReq && state.xp >= ach.xpReq) earned = true;
    if (ach.streak && state.streak >= ach.streak) earned = true;
    if (ach.trendCount && (state.trendUseCount || 0) >= ach.trendCount) earned = true;
    if (ach.collabCount && (state.collabSuggestCount || 0) >= ach.collabCount) earned = true;

    if (earned) {
      state.milestones.push(ach.id);
      queueAchievement(ach);
    }
  }
}

// ── Add XP for an action ──────────────────────────────────────────────────────
export function addXP(action, meta = {}) {
  const state = getXPState();
  const baseXP = XP_ACTIONS[action] || 0;
  if (!baseXP) return state;

  // One-time milestones
  if (['first_episode', 'niche_setup', 'fifth_episode', 'tenth_episode'].includes(action)) {
    if (state.milestones.includes(action)) return state;
    state.milestones.push(action);
  }

  // Track trend usage
  if (action === 'trend_used') {
    state.trendUseCount = (state.trendUseCount || 0) + 1;
  }

  // Streak multiplier
  const streakMultiplier = Math.min(2.0, 1.0 + (state.streak * 0.1));
  const earnedXP = Math.round(baseXP * streakMultiplier);

  state.xp += earnedXP;
  state.actions.push({ action, xp: earnedXP, ts: Date.now(), ...meta });
  if (state.actions.length > 100) state.actions = state.actions.slice(-100);

  // Check achievements
  checkAchievements(state);

  saveXPState(state);
  return { ...state, earned: earnedXP };
}

// ── Update streak ─────────────────────────────────────────────────────────────
export function updateStreak() {
  const state = getXPState();
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  if (state.lastDate === today) return state;

  if (state.lastDate === yesterday) {
    state.streak += 1;
  } else {
    state.streak = 1;
  }

  state.lastDate = today;
  saveXPState(state);

  addXP('streak_day');
  if (state.streak === 7) addXP('streak_bonus_7');
  if (state.streak === 30) addXP('streak_bonus_30');

  return state;
}

// ── Get current rank ──────────────────────────────────────────────────────────
export function getRank(xp) {
  let rank = RANKS[0];
  for (const r of RANKS) {
    if (xp >= r.xp) rank = r;
    else break;
  }
  return rank;
}

export function getNextRank(xp) {
  for (const r of RANKS) {
    if (xp < r.xp) return r;
  }
  return null;
}

export function getRankProgress(xp) {
  const current = getRank(xp);
  const next = getNextRank(xp);
  if (!next) return 1;
  return Math.min(1, (xp - current.xp) / (next.xp - current.xp));
}

export function checkMilestones(episodeCount) {
  if (episodeCount === 1) addXP('first_episode');
  if (episodeCount === 5) addXP('fifth_episode');
  if (episodeCount === 10) addXP('tenth_episode');
}

// ── Collab suggest (clips feed) — increments counter, awards base XP,
//    and queues milestone bonuses + credit rewards when 10/20/50/100
//    is hit. The credit reward itself is server-side (admin RPC), so
//    we stash a pending-rewards entry that the Settings UI surfaces;
//    a follow-up PR will add the actual server trigger. ────────────
export function recordCollabSuggest(meta = {}) {
  const state = getXPState();
  state.collabSuggestCount = (state.collabSuggestCount || 0) + 1;
  saveXPState(state);

  // Base XP for the action (uses streak multiplier via addXP).
  const after = addXP('collab_suggest', meta);
  // addXP saved its own state — re-read so we don't drop the
  // collabSuggestCount we just bumped.
  const merged = { ...getXPState(), earned: after.earned };

  // Milestone bonuses — XP + a pending credit reward each tier.
  for (const m of COLLAB_MILESTONES) {
    if (merged.collabSuggestCount === m.count
        && !merged.pendingCreditRewards.some(r => r.milestoneId === m.xpKey)) {
      // Award XP for the milestone.
      const milestoneAfter = addXP(m.xpKey, { milestone: m.xpKey });
      // Stash the credit reward as pending until a server-side trigger
      // grants it via admin_grant_credits. The Settings UI shows
      // pending rewards so users see what's owed.
      const cur = getXPState();
      cur.pendingCreditRewards.push({
        milestoneId: m.xpKey,
        credits: m.credits,
        ts: Date.now(),
      });
      saveXPState(cur);
      // Surface a celebratory popup via the existing achievement queue.
      queueAchievement({
        id: `collab_${m.count}`,
        title: m.title,
        desc: `${m.desc} · +${milestoneAfter.earned} XP · ${m.credits} credits pending`,
      });
    }
  }
  return getXPState();
}

// ── UI helper — single source of truth for XP & Achievements section.
//    Returns a snapshot the SettingsPage XP card can render directly.
export function getXPSummary() {
  const state = getXPState();
  const rank = getRank(state.xp);
  const next = getNextRank(state.xp);
  const progress = getRankProgress(state.xp);
  const earnedAchievements = ACHIEVEMENTS.filter(a => state.milestones.includes(a.id));
  const lockedAchievements  = ACHIEVEMENTS.filter(a => !state.milestones.includes(a.id));
  const collabNext = COLLAB_MILESTONES.find(m => state.collabSuggestCount < m.count) || null;
  return {
    xp: state.xp,
    rank,
    nextRank: next,
    progress,
    streak: state.streak,
    earned: earnedAchievements,
    locked: lockedAchievements,
    collabSuggestCount: state.collabSuggestCount || 0,
    collabNextMilestone: collabNext,
    pendingCreditRewards: state.pendingCreditRewards || [],
    milestones: COLLAB_MILESTONES,
    recentActions: (state.actions || []).slice(-10).reverse(),
  };
}

export { RANKS, XP_ACTIONS, ACHIEVEMENTS, COLLAB_MILESTONES };
